import { ModalButtonStyles, OpenModal, useTable } from '@composables/common'
import UpdateOrCreatePlatformDocumentForm from '@partials/platforms/UpdateOrCreatePlatformDocumentForm.vue'
import { NameAndId } from '@src/components'
import { PlatformDocument } from '@src/types'
import { uploadPrivate } from '@src/utils/api/s3'
import { useLanguageStore } from '@src/utils/stores/languages'
import { useUserStore } from '@src/utils/stores/user'
import { Ref, computed, h, ref } from 'vue'
import { useI18n } from 'vue-i18n'

export function usePlatformDocumentsTable(
    documents: Ref<PlatformDocument[]>,
    update: (successMessage?: string | null) => Promise<void>,
    openModal: OpenModal,
    isModalOpen: Ref<boolean>
) {
    const userStore = useUserStore()
    const i18n = useI18n()
    const languageStore = useLanguageStore()

    const total = computed(() => {
        return documents.value.length
    })

    const {
        TableWrapper: PlatformDocumentsTable,
        selected: selectedPlatformDocumentsIds,
    } = useTable({
        tableProps: {
            id: 'platform_documents',
            label: 'platform_documents',
            help: 'help_platform_documents_table',
            data: documents,
            total: total,
            columns: [
                {
                    label: 'id',
                    key: 'id',
                    select: true,
                },
                {
                    label: 'id',
                    key: 'id',
                    condition: userStore.can('see_id_columns'),
                    copy: true,
                },
                {
                    label: 'name',
                    key: 'name',
                },
                {
                    label: 'language',
                    key: 'language',
                    cellRenderer: (props: { rowData: PlatformDocument }) => {
                        return (
                            languageStore.languages.find(
                                (language) =>
                                    language.value === props.rowData.language
                            )?.label || props.rowData.language
                        )
                    },
                },
            ],
            actions: [
                {
                    action: handleViewPlatformDocument,
                    icon: 'fa-eye',
                    title: 'view',
                },
                {
                    action: handleUpdateOrCreatePlatformDocument,
                    icon: 'fa-solid fa-pen',
                    title: 'edit',
                    condition: userStore.can('update_platform'),
                },
                {
                    action: handleDeletePlatformDocument,
                    icon: 'fas fa-trash-alt',
                    title: 'delete',
                    color: 'text-danger-500',
                    condition: userStore.can('update_platform'),
                },
            ],
        },
    })

    function handleViewPlatformDocument(platformDocument: PlatformDocument) {
        const url = platformDocument.url
        if (url) {
            window.open(url)
        }
    }

    function handleUpdateOrCreatePlatformDocument(
        platformDocument: PlatformDocument = new PlatformDocument()
    ) {
        const editedPlatformDocument = ref(new PlatformDocument())
        editedPlatformDocument.value = { ...platformDocument }
        const file = ref<File | null>(null)
        isModalOpen.value = true
        const disableCreate = computed(() => {
            return !(
                editedPlatformDocument.value.upload_key &&
                editedPlatformDocument.value.name &&
                editedPlatformDocument.value.language
            )
        })
        openModal(
            {
                id: 'edit_platform_document',
                title: editedPlatformDocument.value.id
                    ? 'modal_title_update_platform_document'
                    : 'modal_title_create_platform_document',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () =>
                    savePlatformDocument(
                        editedPlatformDocument.value,
                        file.value as File
                    ),
            },
            () =>
                h(UpdateOrCreatePlatformDocumentForm, {
                    // eslint-disable-next-line quote-props
                    modelValue: editedPlatformDocument,
                    'onUpdate:modelValue': (
                        value: unknown,
                        files: Ref<FileList>
                    ) => {
                        file.value = files.value?.item(0)
                    },
                }),
            {
                submitButtonText: editedPlatformDocument.value.id
                    ? 'update'
                    : 'create',
                styles: {
                    submitButton: ModalButtonStyles['BLUE'],
                },
                submitButtonDisabled: disableCreate,
            }
        )
    }

    function handleDeletePlatformDocument(platformDocument: PlatformDocument) {
        isModalOpen.value = true
        openModal(
            {
                id: 'delete_platform_document',
                title: 'modal_title_delete_platform_document',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () => deletePlatformDocument(platformDocument),
            },
            () =>
                h(NameAndId, {
                    idKey: 'id',
                    nameKey: 'name',
                    modelValue: [platformDocument],
                }),
            {
                submitButtonText: 'delete',
                styles: {
                    submitButton: ModalButtonStyles['RED'],
                },
            }
        )
    }

    function handleDeletePlatformDocuments() {
        isModalOpen.value = true
        openModal(
            {
                id: 'delete_platform_documents',
                title: 'modal_title_delete_platform_documents',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () => deletePlatformDocuments(),
            },
            () =>
                h(NameAndId, {
                    idKey: 'id',
                    nameKey: 'name',
                    modelValue: selectedPlatformDocuments.value,
                }),
            {
                submitButtonText: 'delete',
                styles: {
                    submitButton: ModalButtonStyles['RED'],
                },
            }
        )
    }

    function deletePlatformDocument(
        platformDocumentToDelete: PlatformDocument,
        submit = true
    ) {
        const documentsTemp = [...documents.value]
        documents.value.splice(
            documents.value.findIndex(
                (platformDocument) =>
                    platformDocumentToDelete.id == platformDocument.id
            ),
            1
        )
        if (submit) {
            update(
                i18n.t('toast_success_platform_document_deleted', {
                    document: platformDocumentToDelete.name,
                })
            )
                .catch((error) => {
                    documents.value = documentsTemp
                    throw error
                })
                .then(() => {
                    selectedPlatformDocumentsIds.value = []
                    isModalOpen.value = false
                })
        }
    }

    function deletePlatformDocuments() {
        const documentsTemp = [...documents.value]
        selectedPlatformDocuments.value.forEach((platformDocument) => {
            deletePlatformDocument(platformDocument, false)
        })

        update(
            i18n.t('toast_success_platform_documents_deleted', {
                documents: selectedPlatformDocuments.value
                    .map((platformDocument) => platformDocument.name)
                    .join(', '),
            })
        )
            .catch((error) => {
                documents.value = documentsTemp
                throw error
            })
            .then(() => {
                selectedPlatformDocumentsIds.value = []
                isModalOpen.value = false
            })
    }

    async function savePlatformDocument(
        platformDocument: PlatformDocument,
        file: File
    ) {
        const documentsTemp = [...documents.value]
        if (file) {
            const uploadPrivateResponse = await uploadPrivate(file)
            platformDocument.upload_key = uploadPrivateResponse.data.key
            platformDocument.url = uploadPrivateResponse.data.temp_storage_url
        }

        if (platformDocument.id) {
            documents.value[
                documents.value.findIndex(
                    (document: PlatformDocument) =>
                        platformDocument.id == document.id
                )
            ] = platformDocument
        } else {
            documents.value.push(platformDocument)
        }

        let toast = 'toast_success_platform_document_updated'
        if (!platformDocument.id) {
            toast = 'toast_success_platform_document_created'
        }
        update(
            i18n.t(toast, {
                document: platformDocument.name,
            })
        )
            .catch((error) => {
                documents.value = documentsTemp
                throw error
            })
            .then(() => {
                isModalOpen.value = false
            })
    }

    const selectedPlatformDocuments = computed(() => {
        return documents.value.filter(
            (item: PlatformDocument) =>
                item.id && selectedPlatformDocumentsIds.value.includes(item.id)
        )
    })

    return {
        PlatformDocumentsTable,
        selectedPlatformDocumentsIds,
        handleUpdateOrCreatePlatformDocument,
        handleDeletePlatformDocuments,
    }
}
